import JSZip from "jszip";
import { PDFDocument, PDFFont, PDFPage, StandardFonts, rgb } from "pdf-lib";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { StorageBucketsEnum } from "../../types/enums";
import { InvoiceType } from "../../types/database-enums/invoices-enums";
import type { TableInvoiceRow } from "../../pages/debitoren/types/invoice-table.types";
import { formatDateForExport, getRecipientDisplayName } from "./export-utils";

export const MAX_INVOICE_PDFS_PER_ZIP = 2000;
export const MAX_INVOICE_PDF_MEGABYTES_PER_ZIP = 300;
export const MISSING_INVOICE_PDFS_FILENAME = "Fehlende_Rechnungen.pdf";

const INVOICE_PDF_FOLDER = "Rechnungen";
const SIGNING_BATCH_SIZE = 100;
const PARALLEL_DOWNLOADS = 6;
const ATTEMPTS = 3;
const RETRY_DELAY_MS = 500;
const SIGNED_URL_TTL_SECONDS = 3600;
const ESTIMATED_PDF_SIZE_KB = 80;
const UNSAFE_FILENAME_CHARACTERS = /[<>:"\\|?*\p{Cc}]/gu;

type MissingReason =
	| "Kein PDF hinterlegt"
	| "PDF nicht gefunden"
	| "Download fehlgeschlagen";

interface Download {
	result: Blob | MissingReason;
	failure?: string;
}

interface PdfSlot extends Download {
	invoice: TableInvoiceRow;
	file: { path: string; name: string } | null;
}

export interface MissingPdf {
	invoice: TableInvoiceRow;
	reason: MissingReason;
	failure?: string;
}

export interface InvoicePdfProgress {
	loaded: number;
	total: number;
}

export interface InvoicePdfZipSummary {
	added: number;
	missing: number;
}

const hasStoredPdf = (invoice: TableInvoiceRow) => Boolean(invoice.path_name);

const expectsPdf = (invoice: TableInvoiceRow) =>
	hasStoredPdf(invoice) ||
	invoice.invoice_type !== InvoiceType.RUECKVERGUETUNG;

export const countInvoicePdfs = (invoices: TableInvoiceRow[]): number =>
	invoices.filter(hasStoredPdf).length;

export const estimateZipSizeMb = (pdfCount: number): number =>
	Math.max(1, Math.round((pdfCount * ESTIMATED_PDF_SIZE_KB) / 1024));

const toPdfName = (path: string, usedNames: Set<string>): string => {
	const baseName = (path.split("/").pop() || "Rechnung")
		.replace(/\.pdf$/i, "")
		.replace(UNSAFE_FILENAME_CHARACTERS, "_");
	let name = `${baseName}.pdf`;
	for (let suffix = 2; usedNames.has(name.toLowerCase()); suffix++) {
		name = `${baseName}_${suffix}.pdf`;
	}
	usedNames.add(name.toLowerCase());
	return name;
};

const wait = (ms: number, signal: AbortSignal) =>
	new Promise<void>((resolve, reject) => {
		signal.throwIfAborted();
		const handleAbort = () => {
			clearTimeout(timer);
			reject(signal.reason);
		};
		const timer = setTimeout(() => {
			signal.removeEventListener("abort", handleAbort);
			resolve();
		}, ms);
		signal.addEventListener("abort", handleAbort, { once: true });
	});

const signBatch = async (paths: string[], signal: AbortSignal) => {
	for (let attempt = 1; ; attempt++) {
		const { data, error } = await supabase.storage
			.from(StorageBucketsEnum.V1)
			.createSignedUrls(paths, SIGNED_URL_TTL_SECONDS);
		signal.throwIfAborted();
		if (!error) {
			return new Map(data.map((entry) => [entry.path, entry.signedUrl]));
		}
		if (attempt === ATTEMPTS) {
			Logger.error(error, { identifier: "invoice-pdf-zip-signing" });
			throw new Error(
				"Rechnungs-PDFs konnten nicht geladen werden. Bitte versuchen Sie es erneut."
			);
		}
		await wait(RETRY_DELAY_MS * attempt, signal);
	}
};

const downloadPdf = async (
	url: string,
	signal: AbortSignal
): Promise<Download> => {
	for (let attempt = 1; ; attempt++) {
		let failure: string;
		try {
			const response = await fetch(url, { signal });
			if (response.ok) return { result: await response.blob() };
			failure = `HTTP ${response.status}`;
			if (response.status !== 429 && response.status < 500) {
				return { result: "PDF nicht gefunden", failure };
			}
		} catch (error) {
			signal.throwIfAborted();
			failure = error instanceof Error ? error.name : String(error);
		}
		if (attempt === ATTEMPTS) {
			return { result: "Download fehlgeschlagen", failure };
		}
		await wait(RETRY_DELAY_MS * attempt, signal);
	}
};

const countFailures = (failures: string[]) =>
	Object.entries(
		failures.reduce<Record<string, number>>((counts, failure) => {
			counts[failure] = (counts[failure] ?? 0) + 1;
			return counts;
		}, {})
	)
		.map(([failure, count]) => `${failure} ×${count}`)
		.join(", ");

const MANIFEST_COLUMNS = [
	{ label: "Nummer", x: 50, width: 130, truncate: false },
	{ label: "Empfänger", x: 185, width: 175, truncate: true },
	{ label: "Datum", x: 365, width: 60, truncate: true },
	{ label: "Grund", x: 430, width: 115, truncate: true },
];
const MANIFEST_PAGE_SIZE: [number, number] = [595.28, 841.89];
const MANIFEST_MARGIN = 50;
const MANIFEST_ROW_HEIGHT = 16;

export const toMissingManifestRows = (missing: MissingPdf[]): string[][] =>
	missing.map(({ invoice, reason }) => [
		invoice.invoice_doc_number || `Rechnung_${invoice.invoice_id}`,
		getRecipientDisplayName(invoice),
		formatDateForExport(invoice.invoice_date),
		reason,
	]);

/** Standard PDF fonts only encode WinAnsi; anything else would make drawText throw. */
const toDrawableText = (
	font: PDFFont,
	text: string,
	size: number,
	maxWidth: number
) => {
	let drawable = [...text]
		.map((char) => {
			try {
				font.encodeText(char);
				return char;
			} catch {
				return "?";
			}
		})
		.join("");
	while (
		drawable.length > 1 &&
		font.widthOfTextAtSize(drawable, size) > maxWidth
	) {
		drawable = `${drawable.slice(0, -2)}…`;
	}
	return drawable;
};

const buildMissingManifestPdf = async (
	missing: MissingPdf[]
): Promise<Uint8Array> => {
	const pdf = await PDFDocument.create();
	const font = await pdf.embedFont(StandardFonts.Helvetica);
	const boldFont = await pdf.embedFont(StandardFonts.HelveticaBold);
	let page: PDFPage = pdf.addPage(MANIFEST_PAGE_SIZE);
	let y = MANIFEST_PAGE_SIZE[1] - MANIFEST_MARGIN;

	page.drawText("Fehlende Rechnungen", {
		x: MANIFEST_MARGIN,
		y,
		size: 14,
		font: boldFont,
	});
	y -= 20;
	page.drawText(
		`${missing.length} ${
			missing.length === 1 ? "Rechnung ist" : "Rechnungen sind"
		} nicht als PDF im Export enthalten.`,
		{ x: MANIFEST_MARGIN, y, size: 10, font, color: rgb(0.3, 0.3, 0.3) }
	);
	y -= 28;

	const drawRow = (cells: string[], rowFont: PDFFont) => {
		MANIFEST_COLUMNS.forEach((column, index) => {
			page.drawText(
				toDrawableText(
					rowFont,
					cells[index],
					9,
					column.truncate ? column.width : Infinity
				),
				{
					x: column.x,
					y,
					size: 9,
					font: rowFont,
				}
			);
		});
		y -= MANIFEST_ROW_HEIGHT;
	};

	const headers = MANIFEST_COLUMNS.map((column) => column.label);
	drawRow(headers, boldFont);
	for (const cells of toMissingManifestRows(missing)) {
		if (y < MANIFEST_MARGIN) {
			page = pdf.addPage(MANIFEST_PAGE_SIZE);
			y = MANIFEST_PAGE_SIZE[1] - MANIFEST_MARGIN;
			drawRow(headers, boldFont);
		}
		drawRow(cells, font);
	}

	return pdf.save();
};

export const createInvoicePdfZip = async ({
	listFile,
	invoices,
	signal,
	onProgress,
}: {
	listFile: { blob: Blob; filename: string };
	invoices: TableInvoiceRow[];
	signal?: AbortSignal;
	onProgress?: (progress: InvoicePdfProgress) => void;
}): Promise<{ blob: Blob; summary: InvoicePdfZipSummary }> => {
	const usedNames = new Set<string>();
	const slots: PdfSlot[] = invoices.filter(expectsPdf).map((invoice) => ({
		invoice,
		file: invoice.path_name
			? {
					path: invoice.path_name,
					name: toPdfName(invoice.path_name, usedNames),
				}
			: null,
		result: invoice.path_name
			? "Download fehlgeschlagen"
			: "Kein PDF hinterlegt",
	}));
	const pending = slots.filter(
		(slot): slot is PdfSlot & { file: NonNullable<PdfSlot["file"]> } =>
			slot.file !== null
	);
	if (pending.length > MAX_INVOICE_PDFS_PER_ZIP) {
		throw new Error(
			`Zu viele Rechnungs-PDFs für einen ZIP-Export (${pending.length.toLocaleString(
				"de-CH"
			)}). Bitte Zeitraum auf höchstens ${MAX_INVOICE_PDFS_PER_ZIP.toLocaleString(
				"de-CH"
			)} Rechnungen eingrenzen.`
		);
	}

	const controller = new AbortController();
	const handleAbort = () => controller.abort(signal?.reason);
	if (signal?.aborted) handleAbort();
	signal?.addEventListener("abort", handleAbort, { once: true });

	try {
		let loaded = 0;
		let loadedBytes = 0;
		onProgress?.({ loaded, total: pending.length });
		for (
			let start = 0;
			start < pending.length;
			start += SIGNING_BATCH_SIZE
		) {
			controller.signal.throwIfAborted();
			const batch = pending.slice(start, start + SIGNING_BATCH_SIZE);
			const signedUrlByPath = await signBatch(
				batch.map((slot) => slot.file.path),
				controller.signal
			);

			let next = 0;
			const downloadNext = async () => {
				while (next < batch.length) {
					const slot = batch[next++];
					const signedUrl = signedUrlByPath.get(slot.file.path);
					const download: Download = signedUrl
						? await downloadPdf(signedUrl, controller.signal)
						: {
								result: "PDF nicht gefunden",
								failure: "not signed",
							};
					controller.signal.throwIfAborted();
					Object.assign(slot, download);
					if (download.result instanceof Blob) {
						loadedBytes += download.result.size;
						if (
							loadedBytes >
							MAX_INVOICE_PDF_MEGABYTES_PER_ZIP * 1024 * 1024
						) {
							const tooLarge = new Error(
								`Die Rechnungs-PDFs sind zusammen grösser als ${MAX_INVOICE_PDF_MEGABYTES_PER_ZIP} MB. Bitte Zeitraum eingrenzen.`
							);
							controller.abort(tooLarge);
							throw tooLarge;
						}
					}
					onProgress?.({ loaded: ++loaded, total: pending.length });
				}
			};
			await Promise.all(
				Array.from(
					{ length: Math.min(PARALLEL_DOWNLOADS, batch.length) },
					downloadNext
				)
			);
		}
	} finally {
		signal?.removeEventListener("abort", handleAbort);
	}
	signal?.throwIfAborted();

	const zip = new JSZip();
	zip.file(listFile.filename, listFile.blob);
	const missing: MissingPdf[] = [];
	for (const { invoice, file, result, failure } of slots) {
		if (typeof result === "string") {
			missing.push({ invoice, reason: result, failure });
		} else if (file) {
			zip.file(`${INVOICE_PDF_FOLDER}/${file.name}`, result);
		}
	}
	if (missing.length > 0) {
		zip.file(
			MISSING_INVOICE_PDFS_FILENAME,
			await buildMissingManifestPdf(missing)
		);
	}
	const failures = missing.flatMap(({ failure }) =>
		failure ? [failure] : []
	);
	if (failures.length > 0) {
		Logger.error(
			new Error(
				`Invoice PDFs missing in export: ${countFailures(failures)}`
			),
			{ identifier: "invoice-pdf-zip-download" }
		);
	}

	const blob = await zip.generateAsync({
		type: "blob",
		compression: "STORE",
	});
	signal?.throwIfAborted();
	return {
		blob,
		summary: {
			added: slots.length - missing.length,
			missing: missing.length,
		},
	};
};
